"use client";

import React, { useState } from "react";
import { Star, Upload, Check, Loader2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Label } from "@/components/ui/label";
const ReviewFormContent = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  const [fileName, setFileName] = useState<string | null>(null);
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in duration-500">
        <div className="size-16 rounded-full bg-[hsl(45,30%,96%)] flex items-center justify-center text-[hsl(45,30%,50%)]">
          <Check className="size-8" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-serif font-bold text-[hsl(222,47%,11%)]">Thank You!</h3>
          <p className="text-[hsl(222,47%,11%)] max-w-md mx-auto">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-[hsl(215,20%,85%)] text-[hsl(222,47%,11%)] hover:bg-[hsl(45,30%,96%)]">
          Submit Another Review
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {}
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[hsl(222,47%,11%)] font-medium">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="border-0 border-b border-[hsl(215,20%,85%)] rounded-none px-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:border-[hsl(45,30%,50%)] transition-colors placeholder:text-[hsl(215,20%,70%)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[hsl(222,47%,11%)] font-medium">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="border-0 border-b border-[hsl(215,20%,85%)] rounded-none px-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:border-[hsl(45,30%,50%)] transition-colors placeholder:text-[hsl(215,20%,70%)]" />
              </FormControl>
              <FormDescription className="text-xs text-[hsl(215,20%,60%)]">
                We'll use this to confirm your review.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="rating" rules={{
      required: "Please select a rating"
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="text-[hsl(222,47%,11%)] font-medium">Rating</FormLabel>
            <FormControl>
              <ToggleGroup type="single" onValueChange={field.onChange} value={field.value} className="justify-start gap-2">
                {[1, 2, 3, 4, 5].map((rating, i) => <ToggleGroupItem key={rating} value={rating.toString()} data-index={i} className="size-10 rounded-full border border-[hsl(215,20%,85%)] data-[state=on]:bg-[hsl(45,30%,50%)] data-[state=on]:text-[hsl(222,47%,11%)] data-[state=on]:border-[hsl(45,30%,50%)] hover:bg-[hsl(45,30%,96%)] hover:text-[hsl(45,30%,50%)] transition-all" aria-label={`Rate ${rating} stars`}>
                    <Star className={`size-5 ${field.value >= rating.toString() ? "fill-current" : ""}`} />
                  </ToggleGroupItem>)}
              </ToggleGroup>
            </FormControl>
            <FormDescription className="text-xs text-[hsl(215,20%,60%)]">
              Select a rating from 1 (lowest) to 5 (highest).
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="review" rules={{
      required: "Review is required",
      minLength: {
        value: 10,
        message: "Review must be at least 10 characters long"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[hsl(222,47%,11%)] font-medium">Your Review</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your experience..." {...field} className="min-h-[120px] border-0 border-b border-[hsl(215,20%,85%)] rounded-none px-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:border-[hsl(45,30%,50%)] resize-y placeholder:text-[hsl(215,20%,70%)]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="photo" render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem>
            <FormLabel className="text-[hsl(222,47%,11%)] font-medium">Upload Photo (optional)</FormLabel>
            <FormControl>
              <div className="flex items-center gap-4">
                <label htmlFor="photo-upload" className="flex items-center gap-2 px-4 py-2 border border-dashed border-[hsl(215,20%,60%)] rounded-sm cursor-pointer hover:bg-[hsl(45,30%,96%)] hover:border-[hsl(45,30%,50%)] transition-colors text-sm text-[hsl(215,20%,40%)]">
                  <Upload className="size-4" />
                  {fileName || "Choose file"}
                </label>
                <Input id="photo-upload" type="file" accept="image/*" {...field} onChange={e => {
            const file = e.target.files?.[0];
            if (file) {
              setFileName(file.name);
              onChange(file);
            }
          }} className="hidden" />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="consent" rules={{
      required: "You must consent to publish your review"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0">
            <FormControl>
              <input type="checkbox" checked={field.value} onChange={field.onChange} className="mt-1 size-4 rounded-sm border border-[hsl(215,20%,60%)] text-[hsl(45,30%,50%)] focus:ring-[hsl(45,30%,50%)] cursor-pointer accent-[hsl(45,30%,50%)]" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-sm font-normal text-[hsl(215,20%,40%)] cursor-pointer">
                I consent to publishing my review
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>} />

      {}
      {submitError && <div className="p-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-sm">
          {submitError}
        </div>}

      {}
      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px] h-12 bg-[hsl(45,30%,50%)] hover:bg-[hsl(45,30%,45%)] text-[hsl(222,47%,11%)] font-medium rounded-[2px] shadow-sm hover:-translate-y-1 transition-all duration-300 ease-out">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-4 animate-spin" />
            Submitting...
          </> : "Submit Review"}
      </Button>
    </Form>;
};
export default function LeaveAReview() {
  return <section id="leave-a-review" className="py-20 md:py-28 bg-white relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[hsl(215,20%,85%)] to-transparent opacity-50" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[hsl(215,20%,85%)] to-transparent opacity-50" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-3xl mx-auto">
          {}
          <div className="text-center mb-12 space-y-4">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-[hsl(222,47%,11%)] tracking-tight">
              Leave a Review
            </h2>
            <p className="text-lg text-[hsl(222,47%,11%)] font-sans max-w-xl mx-auto leading-relaxed">
              Thank you for choosing SimProTiling. Your feedback helps us maintain our high standards and helps others make informed decisions.
            </p>
          </div>

          {}
          <div className="bg-white border border-[hsl(215,20%,85%)] p-8 md:p-12 relative">
            {}
            <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[hsl(45,30%,50%)] -mt-[1px] -ml-[1px]" />
            <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[hsl(45,30%,50%)] -mt-[1px] -mr-[1px]" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[hsl(45,30%,50%)] -mb-[1px] -ml-[1px]" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[hsl(45,30%,50%)] -mb-[1px] -mr-[1px]" />

            <FormProvider formId="review_form_leaveareview" sectionName="leave-a-review" defaultValues={{
            name: "",
            email: "",
            rating: "",
            review: "",
            photo: null,
            consent: false
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            rating: {
              required: true
            },
            review: {
              required: true,
              minLength: 10
            },
            consent: {
              required: true
            }
          }} submitText="Submit Review" successMessage="Your review has been submitted successfully. We appreciate your feedback!" errorMessage="There was a problem submitting your review. Please try again.">
              <ReviewFormContent />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}